import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Reveal";
import { WhatsAppNavLink } from "../components/WhatsAppButton";
import api from "../lib/api";
import { CONTACT } from "../data/content";

const PROP_TYPES = ["Apartment", "Villa", "Office", "Commercial"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "", property_type: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please add your name and mobile number."); return; }
    setSending(true);
    try {
      await api.post("/leads", { ...form, source: "contact" });
      toast.success("Message sent! Our team will contact you shortly.");
      setForm({ name: "", mobile: "", email: "", city: "", property_type: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div className="pt-32 pb-24">
      <section className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><SectionLabel>Contact Us</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-5xl md:text-6xl mt-6 leading-tight">Let's build your wish.</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-muted-foreground leading-relaxed max-w-md">
              Share a few details and our design team will get back within one business day to schedule
              a consultation and site visit.
            </p>
          </Reveal>

          <div className="mt-12 space-y-6">
            {[
              { icon: Phone, label: "Call", value: CONTACT.phoneDisplay, href: CONTACT.telHref },
              { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.mailHref },
              { icon: MapPin, label: "Studio", value: CONTACT.address },
              { icon: Clock, label: "Hours", value: CONTACT.hours },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 0.06} className="flex items-start gap-4">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-primary text-primary-foreground shrink-0"><c.icon size={20} /></span>
                <div>
                  <p className="label-caps text-muted-foreground">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="text-lg hover:text-accent transition-colors">{c.value}</a>
                  ) : (
                    <p className="text-lg">{c.value}</p>
                  )}
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.28}>
              <WhatsAppNavLink className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 text-sm font-semibold shadow-[0_6px_20px_rgba(37,211,102,0.35)] hover:bg-[#20bd5a] transition-colors mt-2" />
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="rounded-3xl border border-border bg-card p-8 md:p-10 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name*" className="rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent transition-colors" />
              <input data-testid="contact-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="Mobile number*" className="rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent transition-colors" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className="rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent transition-colors" />
              <input data-testid="contact-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City / location" className="rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent transition-colors" />
            </div>
            <div>
              <p className="label-caps text-muted-foreground mb-3">Property type</p>
              <div className="flex flex-wrap gap-2">
                {PROP_TYPES.map((p) => (
                  <button key={p} type="button" data-testid={`prop-${p.toLowerCase()}`} onClick={() => set("property_type", p)}
                    className={`rounded-full px-4 py-2 text-sm border transition-colors ${form.property_type === p ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-accent"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="Tell us about your project…" className="w-full rounded-xl border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-accent transition-colors resize-none" />
            <button data-testid="contact-submit" disabled={sending} className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-medium hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50">
              {sending ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
